import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import SensorModel from '../../../../../services/sensor.model';
import {SensorService} from '../../../../../services/sensor.service';
import SensorTypeModel from '../../../../../services/sensorType.model';
import {SensorTypeService} from '../../../../../services/sensorType.service';
import QueryParamsModel from '../../../../../services/queryParams.model';
import {QueryParamsService} from '../../../../../services/queryParams.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * SensorComponent
 *
 * This component is responsible for managing Sensors within the Hardware Management module.
 * It provides a user interface for administrators to view, create, update, and search for sensors.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all sensors.
 * - Provide search functionality to filter sensors by name, vendor name, QP name, or sensor type name.
 * - Manage the creation of new sensors with validation.
 * - Handle updates to existing sensors.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update sensors.
 * - **Real-time Search**: Filter list by multiple sensor attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures sensor details follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 * - **Dynamic Dropdowns**: Loads sensor types and query parameters for selection.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all sensors from `SensorService`.
 * 3. Fetches reference data (Sensor Types, Query Params).
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdateSensor` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-sensor
 * @example
 * <app-sensor></app-sensor>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-sensor',
  templateUrl: './sensor.component.html',
  styleUrls: ['./sensor.component.scss']
})
export class SensorComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched sensors from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {SensorModel[]}
   * @default []
   */
  public sensors: SensorModel[] = [];

  /**
   * Array holding the currently displayed sensors.
   * This list is populated by the BatchLoader (subset of sensors or filteredSensors).
   * @type {SensorModel[]}
   * @default []
   */
  public sensorsList: SensorModel[] = [];

  /**
   * Model for the current sensor being added or edited.
   * Bound to the form for data manipulation.
   * @type {SensorModel}
   */
  public sensor: SensorModel = new SensorModel();

  /**
   * Array holding sensors that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {SensorModel[]}
   * @default []
   */
  public filteredSensors: SensorModel[] = [];

  /**
   * Array of available Sensor Types for selection.
   * @type {Array<SensorTypeModel>}
   */
  public sensorTypes: Array<SensorTypeModel>;

  /**
   * Selected Sensor Type model.
   * @type {SensorTypeModel}
   */
  public sensorType: SensorTypeModel;

  /**
   * Array of available Query Parameters for selection.
   * @type {Array<QueryParamsModel>}
   */
  public queryParams: Array<QueryParamsModel>;

  /**
   * Selected Query Parameter model.
   * @type {QueryParamsModel}
   */
  public queryParam: QueryParamsModel;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public sensorDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number (not actively used in current logic but declared).
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for sensor details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for SensorComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - vendor_name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - qp_id: Required.
   * - sensor_type_id: Required.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {SensorService} sensorService - Service for Sensor API operations.
   * @service SensorService
   * @param {QueryParamsService} queryParamsService - Service for Query Params API operations.
   * @service QueryParamsService
   * @param {SensorTypeService} sensorTypeService - Service for Sensor Type API operations.
   * @service SensorTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public sidenavaction: Sidenavaction, public sensorService: SensorService,
              public queryParamsService: QueryParamsService, public sensorTypeService: SensorTypeService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.sensor = new SensorModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      qp_id: ['', [Validators.required]],
      sensor_type_id: ['', [Validators.required]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all sensors from the backend.
   * 5. Fetches reference data (Query Params, Sensor Types).
   * 6. Formats the 'updatedAt' date for each sensor.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.sensors = [];
      this.sensorsList = [];
      this.filteredSensors = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.sidenavaction.x_navigation();
      this.sensors = await this.sensorService.getAllTSensors();
      this.queryParams = await this.queryParamsService.getAllQueryParam();
      this.sensorTypes = await this.sensorTypeService.getAllSensorTypes();
      for (let i = 0; i < this.sensors.length; i++) {
        if (this.sensors[i].updatedAt) {
          const timeFormat = moment(this.sensors[i].updatedAt).format('DD-MMM-YYYY hh:mm:ss A');
          this.sensors[i].updatedAt = JSON.parse(JSON.stringify(timeFormat));
        }
        this.sensorType = this.getSensorTypeById(JSON.stringify(this.sensors[i].sensor_type_id));
        this.sensors = JSON.parse(JSON.stringify(this.sensors));
        this.sensors[i].sensorType = this.sensorType;
        this.queryParam = this.getQueryParamById(JSON.stringify(this.sensors[i].qp_id));
        this.sensors[i].qp = this.queryParam;
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSensors : this.sensors,
      (updatedList) => {
        this.sensorsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.sensorService.cancelOnGoingHttpRequests();
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.sensorTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the sensor list based on the search query.
   *
   * - Filters by name, vendor name, QP name, or sensor type name (case-insensitive).
   * - Updates `filteredSensors`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSensors`, `sensorsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.sensors.filter(sensor =>
        sensor.name?.toLowerCase().includes(query) || sensor.qp?.name?.toLowerCase().includes(query) || sensor.vendor_name?.toLowerCase().includes(query) || sensor.sensorType?.name?.toLowerCase().includes(query)
      )
      : this.sensors;
    this.filteredSensors = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.sensorsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm =false;
    this.sensor = new SensorModel();
    this.sensorDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Sensor form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateSensor
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateSensor() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    const loginUser = localStorage.getItem('LoginUserName');
    formData.created_name = loginUser;
    formData.updated_name = loginUser;
    try {
      this.sensorDoubleClick = true;
      if (this.submitButton) {
        await this.sensorService.create(formData);
        this.toastrService.success('Sensor created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSensor = {...formData};
        delete updateSensor.id;
        await this.sensorService.updateOneByID(this.sensor.id, updateSensor);
        this.toastrService.success('Sensor updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.sensorDoubleClick = false;
    }
  }

  /**
   * Retrieves a Sensor by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSensorById
   * @param {string} id - The ID of the sensor to edit.
   * @returns {void}
   */
  public getSensorById(id: string) {
   this.showForm = true;
   this.submitButton = false;
    this.sensorDoubleClick = false;
    this.updateButton = true;
    const foundSensors = this.sensorsList.find(uom => uom?.id === id);
    this.sensor = foundSensors ? {...foundSensors} : new SensorModel(); // clone object
    if (this.sensor) {
      this.registerForm.patchValue({
        name: this.sensor.name,
        vendor_name: this.sensor.vendor_name,
        qp_id: this.sensor.qp.id,
        sensor_type_id: this.sensor.sensorType.id
      });
    }
  }

  /**
   * Retrieves a Sensor Type by ID from the local list.
   *
   * @method getSensorTypeById
   * @param {string} id - The ID of the Sensor Type.
   * @returns {SensorTypeModel} The found Sensor Type model.
   */
  public getSensorTypeById(id: string) {
    this.sensorType = this.sensorTypes.find(sensorType => sensorType && sensorType.id == id);
    return this.sensorType;
  }

  /**
   * Retrieves a Query Parameter by ID from the local list.
   *
   * @method getQueryParamById
   * @param {string} id - The ID of the Query Parameter.
   * @returns {QueryParamsModel} The found Query Parameter model.
   */
  public getQueryParamById(id: string) {
    this.queryParam = this.queryParams.find(queryParam => queryParam && queryParam.id == id);
    return this.queryParam;
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
